/**
 * Pure route-optimization solver (M8-B) — the OSS-first, fully client-computable
 * core the `haversineRoutingProvider` wraps. It builds a distance/time matrix
 * from great-circle distance and orders the stops with a nearest-neighbor seed
 * refined by 2-opt. No external call, no vendor: route optimization is REAL and
 * verifiable now (a real Valhalla/OSRM adapter later just supplies a road-network
 * matrix into the same solver).
 *
 * The tour is an OPEN path (visit each stop once, no forced return to start).
 * We seed 2-opt from BOTH the input (naive) order and the nearest-neighbor tour
 * and keep the shorter — so the optimized total is guaranteed ≤ the naive-order
 * total (the "totals monotonic vs naive" invariant the tests assert).
 */

import { haversineMeters } from "./geo";
import { DEFAULT_ROUTE_SPEED_KMH, type OptimizeResult, type RouteMatrix, type RouteStop } from "./types";

/** Numeric slack so floating-point ties don't churn the 2-opt loop. */
const EPS = 1e-6;
/** Safety cap on 2-opt passes (converges well under this for ≤70 stops). */
const MAX_TWO_OPT_PASSES = 80;

/** Build the full great-circle distance (m) + duration (s) matrix for a stop set. */
export function buildHaversineMatrix(stops: RouteStop[], speedKmh = DEFAULT_ROUTE_SPEED_KMH): RouteMatrix {
  const n = stops.length;
  const mps = (speedKmh * 1000) / 3600;
  const distances: number[][] = Array.from({ length: n }, () => new Array<number>(n).fill(0));
  const durations: number[][] = Array.from({ length: n }, () => new Array<number>(n).fill(0));
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const d = haversineMeters(stops[i]!.coord, stops[j]!.coord);
      distances[i]![j] = d;
      distances[j]![i] = d;
      const t = mps > 0 ? d / mps : 0;
      durations[i]![j] = t;
      durations[j]![i] = t;
    }
  }
  return { ids: stops.map((s) => s.id), distances, durations };
}

/** Total length of an open path (sum of consecutive legs) over a distance matrix. */
export function pathLength(order: number[], d: number[][]): number {
  let sum = 0;
  for (let k = 0; k < order.length - 1; k++) sum += d[order[k]!]![order[k + 1]!]!;
  return sum;
}

/** Greedy nearest-neighbor visiting order from `start` over the distance matrix. */
export function nearestNeighborOrder(d: number[][], start = 0): number[] {
  const n = d.length;
  if (n === 0) return [];
  const visited = new Array<boolean>(n).fill(false);
  const order = [start];
  visited[start] = true;
  for (let step = 1; step < n; step++) {
    const last = order[order.length - 1]!;
    let best = -1;
    let bestD = Infinity;
    for (let j = 0; j < n; j++) {
      if (visited[j]) continue;
      const dist = d[last]![j]!;
      if (dist < bestD) {
        bestD = dist;
        best = j;
      }
    }
    if (best < 0) break;
    visited[best] = true;
    order.push(best);
  }
  return order;
}

/**
 * 2-opt local search over an OPEN path. Repeatedly reverses a segment [i..j]
 * when doing so shortens the path, until no reversal improves it (or the pass
 * cap is hit). Endpoint edges are handled explicitly so an open path is
 * optimized, not a closed loop.
 */
export function twoOpt(initial: number[], d: number[][]): number[] {
  const n = initial.length;
  if (n < 4) return initial.slice();
  const best = initial.slice();
  let improved = true;
  let passes = 0;
  while (improved && passes < MAX_TWO_OPT_PASSES) {
    improved = false;
    passes++;
    for (let i = 1; i < n - 1; i++) {
      for (let j = i + 1; j < n; j++) {
        const a = best[i - 1]!;
        const b = best[i]!;
        const c = best[j]!;
        const hasTail = j + 1 < n;
        const e = hasTail ? best[j + 1]! : -1;
        const before = d[a]![b]! + (hasTail ? d[c]![e]! : 0);
        const after = d[a]![c]! + (hasTail ? d[b]![e]! : 0);
        if (after + EPS < before) {
          // reverse best[i..j] in place
          let lo = i;
          let hi = j;
          while (lo < hi) {
            const tmp = best[lo]!;
            best[lo] = best[hi]!;
            best[hi] = tmp;
            lo++;
            hi--;
          }
          improved = true;
        }
      }
    }
  }
  return best;
}

/**
 * Optimize the visiting order for a stop set. Returns the ordered stop ids, the
 * matrix, and the leg totals. Guarantees `totals.distance ≤` the naive
 * (input-order) total by seeding 2-opt from the input order too.
 */
export function optimizeStops(stops: RouteStop[], speedKmh = DEFAULT_ROUTE_SPEED_KMH): OptimizeResult {
  const matrix = buildHaversineMatrix(stops, speedKmh);
  const n = stops.length;
  if (n <= 1) {
    return { order: stops.map((s) => s.id), matrix, totals: { distance: 0, duration: 0 } };
  }
  const d = matrix.distances;
  const identity = stops.map((_, i) => i);
  // Candidates: refine BOTH the naive order and the NN tour; keep the shorter.
  const candidates = [twoOpt(identity, d), twoOpt(nearestNeighborOrder(d, 0), d)];
  let bestOrder = candidates[0]!;
  let bestLen = pathLength(bestOrder, d);
  for (const cand of candidates.slice(1)) {
    const len = pathLength(cand, d);
    if (len < bestLen) {
      bestLen = len;
      bestOrder = cand;
    }
  }
  let distance = 0;
  let duration = 0;
  for (let k = 0; k < bestOrder.length - 1; k++) {
    const from = bestOrder[k]!;
    const to = bestOrder[k + 1]!;
    distance += matrix.distances[from]![to]!;
    duration += matrix.durations[from]![to]!;
  }
  return { order: bestOrder.map((i) => stops[i]!.id), matrix, totals: { distance, duration } };
}
